import { ReactNode, useId, useMemo, useState } from "react";
import { Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { EmptyState } from "@/pages/CustomerPortal/Financial/component/EmptyState";
import { PAGE_SIZES } from "./pagination";
import { TablePagination } from "./TablePagination";

export interface SearchableColumn<T> {
  id: string;
  header: string;
  cell: (row: T) => ReactNode;
  /** Texto pesquisado nesta coluna; sem ele, a coluna não entra na pesquisa. */
  searchValue?: (row: T) => string | number | null | undefined;
  className?: string;
}

interface Props<T> {
  rows: T[];
  columns: SearchableColumn<T>[];
  rowKey: (row: T, index: number) => string;
  /** O que a lista mostra, para o rótulo da pesquisa (ex.: "clientes"). */
  itemLabel: string;
  emptyTitle: string;
  emptyMessage: string;
}

const ALL_COLUMNS = "all";

// Sem acento e sem diferenciar maiúsculas.
const normalize = (value: string) => value.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
const digitsOf = (value: string) => value.replace(/\D/g, "");

/**
 * Busca com 3 dígitos ou mais também compara só os dígitos, para achar CNPJ e código com ou sem máscara
 * ("07738004" acha "07.738.004/0001-10").
 */
function matches(value: string, query: string, queryDigits: string): boolean {
  if (normalize(value).includes(query)) return true;
  return queryDigits.length >= 3 && digitsOf(value).includes(queryDigits);
}

/**
 * Tabela da tela de importação com pesquisa (em todas as colunas ou em uma) e paginação, feitas na tela. Defina as
 * colunas fora do componente que usa a tabela, para a pesquisa não ser refeita a cada render.
 */
export function SearchableTable<T>({ rows, columns, rowKey, itemLabel, emptyTitle, emptyMessage }: Props<T>) {
  const searchId = useId();
  const columnId = useId();
  const [query, setQuery] = useState("");
  const [column, setColumn] = useState(ALL_COLUMNS);
  const [pageSize, setPageSize] = useState(PAGE_SIZES[0]);
  const [pageIndex, setPageIndex] = useState(0);

  const searchableColumns = useMemo(() => columns.filter((item) => item.searchValue), [columns]);

  const filtered = useMemo(() => {
    const normalizedQuery = normalize(query.trim());
    if (!normalizedQuery) return rows;
    const queryDigits = /^[\d\s./-]+$/.test(query.trim()) ? digitsOf(query) : "";
    const targets = column === ALL_COLUMNS ? searchableColumns : searchableColumns.filter((item) => item.id === column);
    return rows.filter((row) =>
      targets.some((target) => matches(String(target.searchValue!(row) ?? ""), normalizedQuery, queryDigits)),
    );
  }, [rows, query, column, searchableColumns]);

  if (rows.length === 0) {
    return <EmptyState title={emptyTitle} message={emptyMessage} className="py-6" />;
  }

  const pageCount = Math.max(1, Math.ceil(filtered.length / pageSize));
  const page = Math.min(pageIndex, pageCount - 1);
  const shown = filtered.slice(page * pageSize, (page + 1) * pageSize);

  return (
    <div className="space-y-3 pt-2">
      <div className="flex flex-col gap-2 sm:flex-row">
        <div className="relative flex-1">
          <Label htmlFor={searchId} className="sr-only">Pesquisar {itemLabel}</Label>
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
          <Input
            id={searchId}
            placeholder={`Pesquisar ${itemLabel}...`}
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
              setPageIndex(0);
            }}
            className="pl-10"
          />
        </div>
        <div className="sm:w-56">
          <Label htmlFor={columnId} className="sr-only">Coluna da pesquisa</Label>
          <Select
            value={column}
            onValueChange={(value) => {
              setColumn(value);
              setPageIndex(0);
            }}
          >
            <SelectTrigger id={columnId}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL_COLUMNS}>Todas as colunas</SelectItem>
              {searchableColumns.map((item) => (
                <SelectItem key={item.id} value={item.id}>{item.header}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {filtered.length === 0 ? (
        <EmptyState title="Nenhum resultado" message="Nenhuma linha corresponde à pesquisa." className="py-6" />
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              {columns.map((item) => (
                <TableHead key={item.id} scope="col">{item.header}</TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {shown.map((row, index) => (
              <TableRow key={rowKey(row, page * pageSize + index)}>
                {columns.map((item) => (
                  <TableCell key={item.id} className={item.className}>{item.cell(row)}</TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <TablePagination
        page={page + 1}
        pageSize={pageSize}
        total={filtered.length}
        unfilteredTotal={rows.length}
        onPageChange={(next) => setPageIndex(next - 1)}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setPageIndex(0);
        }}
      />
    </div>
  );
}
